import {
  faCalculator,
  faCircleCheck,
  faFeatherPointed,
  faLock,
  faTimesCircle,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';

import CanSyncWithHealthieIcon from '@/components/CanSyncWithHealthieIcon';
import Debug from '@/components/Debug';
import DiscoveryBadge from '@/components/InsuranceSection/DiscoveryBadge';
import RenderRichText from '@/components/RenderRichText';
import { Color } from '@/constants/color';
import { PRIOR_AUTH_OPTIONS } from '@/constants/treatability';
import useAuditLog from '@/hooks/useAuditLog';
import { isInsurancePolicyExpired } from '@/pages/Profile/utils/isInsurancePolicyExpired';
import AuditLogLink from '@/ui/components/AuditLogLink';
import Badge from '@/ui/components/Badge';
import CopyContent from '@/ui/components/CopyContent';
import IconItem from '@/ui/components/IconItem';
import Tooltip from '@/ui/components/Tooltip';
import { currencyFormatter } from '@/utils/currencies';
import { daysBetween, formatNumericCalendarDate } from '@/utils/dates';

import type { Props } from './types';

const VerificationStatuses = {
  VERIFIED: 'Verified',
  FAILED_VERIFICATION: 'Failed Verification',
  PENDING_COB: 'Pending Coordination of Benefits'
  // PENDING_VERIFICATION: 'Pending Verification',
  // BILLING_REQUIREMENTS_MET: 'Billing Requirements Met'
};

const VerificationStatusStyles = {
  [VerificationStatuses.VERIFIED]: 'text-blue-800',
  [VerificationStatuses.FAILED_VERIFICATION]: 'text-red-800',
  [VerificationStatuses.PENDING_COB]: 'text-red-800'
  // [VerificationStatuses.PENDING_VERIFICATION]: 'text-yellow-800',
  // [VerificationStatuses.BILLING_REQUIREMENTS_MET]: 'text-blue-800'
};

export default function InsurancePolicy({ insurance_policy, mostRecentDiscovery }: Props) {
  const { insurance_card_images, is_self_pay, plan_end_date, plan_start_date } = insurance_policy;
  const planExpired = isInsurancePolicyExpired(plan_end_date);

  const { data: auditLogData } = useAuditLog(
    {
      appLabel: 'insurance',
      model: 'insurancepolicy',
      objectId: insurance_policy.id || ''
    },
    !!insurance_policy.id
  );
  const {
    verified_by,
    verified_via,
    verified_at,
    status: verified_status,
    deductible_remaining,
    service_code,
    vob_notes,
    verified_through
  } = insurance_policy.last_verification || {};
  const verificationExpired = !!verified_at && +daysBetween(new Date(verified_at)) > 30;

  const { annual_deductible, co_insurance, co_pay, co_pay_unit, out_of_pocket_maximum } =
    insurance_policy;

  const hasPaymentInfo =
    annual_deductible != null ||
    co_insurance != null ||
    (co_pay != null && co_pay_unit) ||
    out_of_pocket_maximum != null ||
    deductible_remaining != null;

  const verified_status_classes: string = verified_status
    ? VerificationStatusStyles[verified_status]
    : '';

  const { icon, color } =
    insurance_policy.has_active_treatability_match && !planExpired
      ? { icon: faCircleCheck, color: 'text-green-500' }
      : { icon: faTimesCircle, color: 'text-red-500' };

  const formatDeductibleRemaining = (deductibleRemaining: number) => {
    return currencyFormatter(deductibleRemaining / 100);
  };

  const isCardUploadedAfterVerification = useMemo(() => {
    if (!insurance_card_images?.updated_at || !verified_at) {
      return false;
    }
    return new Date(insurance_card_images.updated_at) > new Date(verified_at);
  }, [insurance_card_images?.updated_at, verified_at]);

  const renderVerifiedVia = useMemo(() => {
    if (typeof verified_via === 'string') {
      return verified_via;
    }

    return verified_via?.name || 'No Source';
  }, [verified_via]);

  return (
    // FIXME: Send acceptance / waitlist data for each policy.
    <IconItem key={insurance_policy.id} color={color} icon={icon} dataTestId="treatability-icon">
      <div className="flex justify-between flex-wrap">
        <span className="text-lg flex items-center gap-2">
          <span>{insurance_policy.carrier}</span>
          {!insurance_policy.provider_name && (
            <Tooltip tooltipContent="Manually Entered by user">
              <span className="ml-1 text-yellow-700">
                <FontAwesomeIcon icon={faFeatherPointed} />
              </span>
            </Tooltip>
          )}
          {insurance_policy.id && (
            <span className="ml-2">
              <AuditLogLink entityType="insurancepolicy" entityId={insurance_policy.id} />
            </span>
          )}
        </span>

        <div>
          <div className="flex items-center flex-wrap">
            {insurance_policy.was_discovered && mostRecentDiscovery && (
              <span className="mr-4">
                <DiscoveryBadge
                  requestedAt={mostRecentDiscovery.requested_at}
                  source={mostRecentDiscovery.source}
                />
              </span>
            )}
            {hasPaymentInfo && (
              <Tooltip
                tooltipContent={
                  <div>
                    {deductible_remaining != null && (
                      <p>
                        Deductible Remaining: {formatDeductibleRemaining(deductible_remaining)}
                        {verified_at && (
                          <span> as of {new Date(verified_at).toLocaleDateString()}</span>
                        )}
                      </p>
                    )}
                    {annual_deductible != null && (
                      <p>Annual Deductible: {currencyFormatter(annual_deductible)}</p>
                    )}
                    {co_insurance != null && <p>Co-Insurance: {co_insurance * 100}%</p>}
                    {co_pay != null && (
                      <p>
                        Co-Pay: {currencyFormatter(co_pay / 100)}{' '}
                        {co_pay_unit && ` x ${co_pay_unit}`}
                      </p>
                    )}
                    {out_of_pocket_maximum != null && (
                      <p>Out of Pocket Maximum: {currencyFormatter(out_of_pocket_maximum / 100)}</p>
                    )}
                  </div>
                }>
                <span className="flex items-center mr-4">
                  <FontAwesomeIcon className="mr-2" icon={faCalculator} />
                  Benefits
                </span>
              </Tooltip>
            )}

            {is_self_pay ? (
              <span className="text-gray-600">Not Applicable</span>
            ) : verified_status ? (
              <Tooltip
                tooltipContent={
                  <div>
                    {verified_at && <p>At: {new Date(verified_at).toLocaleString()}</p>}
                    {verified_by && (
                      <p>
                        By: {verified_by.first_name} {verified_by.last_name}
                      </p>
                    )}
                    <p>Via: {renderVerifiedVia}</p>
                    {verified_through && <p>Through: {verified_through}</p>}
                    {deductible_remaining && (
                      <p>Deductible Remaining: {formatDeductibleRemaining(deductible_remaining)}</p>
                    )}
                    {service_code && <p>Service Code: {service_code}</p>}
                    {vob_notes && (
                      <p>
                        Notes:
                        <RenderRichText className="prose-invert prose-sm" body={vob_notes} />
                      </p>
                    )}
                  </div>
                }>
                <span className={verified_status_classes}>
                  {!insurance_policy.is_mutable && (
                    <FontAwesomeIcon icon={faLock} className="mr-1 text-black" />
                  )}
                  {verified_status}
                </span>
              </Tooltip>
            ) : (
              <span className="text-gray-600">Pending Verification</span>
            )}

            <div className="ml-4">
              <CanSyncWithHealthieIcon insurancePolicy={insurance_policy} />
            </div>
          </div>
        </div>
      </div>
      {insurance_policy.is_default && <p className="text-gray-600 text-sm">Default</p>}
      {!is_self_pay && insurance_policy.plan && (
        <p>
          <span>Plan: </span>
          <span className="font-bold">
            <CopyContent content={insurance_policy.plan} />
          </span>
        </p>
      )}
      {!is_self_pay && insurance_policy.subscriber_id && (
        <p>
          <span>Subscriber ID: </span>
          <span className="font-bold">
            <CopyContent content={insurance_policy.subscriber_id} />
          </span>
        </p>
      )}
      {insurance_policy.policy_member_id && (
        <p>
          <span>Member ID: </span>
          <span className="font-bold">
            <CopyContent content={insurance_policy.policy_member_id} />
          </span>
        </p>
      )}
      {!is_self_pay && insurance_policy.type && (
        <p>
          <span>Type: </span>
          <span className="font-bold">{insurance_policy.type}</span>
        </p>
      )}
      {!is_self_pay && insurance_policy.priority_type && (
        <p className={insurance_policy.priority_type == 'Inactive' ? 'text-red-600' : ''}>
          <span>Order: </span>
          <span className="font-bold">{insurance_policy.priority_type}</span>
        </p>
      )}
      {verified_at && (
        <p className={`${verificationExpired ? 'text-red-600' : 'text-black'}`}>
          <span>Verified at: </span>
          <span className="font-bold">{new Date(verified_at).toLocaleDateString()}</span>
          {verificationExpired && <span> (&gt;30 days)</span>}
        </p>
      )}
      {plan_start_date && (
        <p>
          <span>Plan Start Date: </span>
          <span className="font-bold">{formatNumericCalendarDate(plan_start_date)}</span>
        </p>
      )}
      {plan_end_date && (
        <p className={`${planExpired ? 'text-red-600' : 'text-black'}`}>
          <span>Plan End Date: </span>
          <span className="font-bold">{formatNumericCalendarDate(plan_end_date)}</span>
        </p>
      )}
      {!is_self_pay && (
        <span>
          Cards:{' '}
          {isCardUploadedAfterVerification && (
            <Tooltip
              tooltipContent={
                <>
                  Card images were uploaded on{' '}
                  <span>
                    {insurance_card_images?.updated_at &&
                      new Date(insurance_card_images.updated_at).toLocaleString()}{' '}
                  </span>
                  after the last verification on{' '}
                  <span>{verified_at && new Date(verified_at).toLocaleString()} </span>
                </>
              }>
              <>
                <FontAwesomeIcon icon={faUpload} className="text-yellow-600 mr-1" />{' '}
              </>
            </Tooltip>
          )}
          <>
            {insurance_card_images?.front_image_url ? (
              <Tooltip
                tooltipContent={
                  <img
                    loading="lazy"
                    src={insurance_card_images?.front_image_url}
                    alt="Front"
                    className="w-50"
                  />
                }>
                <a
                  className="text-blue-800"
                  target="_blank"
                  rel="noreferrer"
                  href={insurance_card_images.front_image_url}>
                  Front
                </a>
              </Tooltip>
            ) : (
              <span className="text-gray-600">No Front</span>
            )}{' '}
            |{' '}
            {insurance_card_images?.back_image_url ? (
              <Tooltip
                tooltipContent={
                  <img
                    loading="lazy"
                    src={insurance_card_images?.back_image_url}
                    alt="Back"
                    className="w-50"
                  />
                }>
                <a
                  className="text-blue-800"
                  target="_blank"
                  rel="noreferrer"
                  href={insurance_card_images.back_image_url}>
                  Back
                </a>
              </Tooltip>
            ) : (
              <span className="text-gray-600">No Back</span>
            )}
            {insurance_card_images?.id && (
              <AuditLogLink
                entityType="insurancecardimage"
                entityId={insurance_card_images.id}
                className="ml-2"
              />
            )}
          </>
        </span>
      )}
      {insurance_policy.prior_auth_required == PRIOR_AUTH_OPTIONS.YES && (
        <p className="my-1">
          <Badge color={Color.RED} text={'Prior Auth Required'} />
        </p>
      )}
      {auditLogData?.data?.created && (
        <p className="text-gray-600 text-xs">
          <span>Created by: </span>
          {auditLogData.data.created.actor ? (
            <Tooltip
              tooltipContent={
                <p>{new Date(auditLogData.data.created.timestamp).toLocaleString()}</p>
              }>
              <span className="font-semibold">{auditLogData.data.created.actor.email}</span>
            </Tooltip>
          ) : (
            <span className="font-semibold">System</span>
          )}
        </p>
      )}
      {auditLogData?.data?.last_updated && (
        <p className="text-gray-600 text-xs">
          <span>Last updated by: </span>
          {auditLogData.data.last_updated.actor ? (
            <Tooltip
              tooltipContent={
                <p>{new Date(auditLogData.data.last_updated.timestamp).toLocaleString()}</p>
              }>
              <span className="font-semibold">{auditLogData.data.last_updated.actor.email}</span>
            </Tooltip>
          ) : (
            <span className="font-semibold">System</span>
          )}
        </p>
      )}
      <Debug object={insurance_policy} />
    </IconItem>
  );
}
